/*
 * KUVRD native wishlist — lightweight, localStorage-only.
 * Replaces Swym "Wishlist Plus". Stores product handles in the browser;
 * no app, no account, no PII. Renders a heart toggle on product pages,
 * a header count badge, and a full list on /pages/wishlist.
 * Independent of the Klaviyo back-in-stock flow (not touched).
 * Header badge styling comes from the theme's .thb-item-count rules
 * (see snippets/wishlist-icon.liquid) — no badge CSS is injected here.
 */
(function () {
  'use strict';

  var STORE_KEY = 'kuvrd:wishlist';

  /* ---------- storage ---------- */
  function read() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  }
  function write(list) {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify(list));
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list: list } }));
  }
  function has(handle) {
    return read().indexOf(handle) !== -1;
  }
  function toggle(handle) {
    if (!handle) return false;
    var list = read();
    var i = list.indexOf(handle);
    if (i === -1) {
      list.push(handle);
      write(list);
      return true;
    }
    list.splice(i, 1);
    write(list);
    return false;
  }
  function remove(handle) {
    var list = read();
    var i = list.indexOf(handle);
    if (i !== -1) {
      list.splice(i, 1);
      write(list);
    }
  }

  window.KuvrdWishlist = { read: read, has: has, toggle: toggle, remove: remove, count: function () { return read().length; } };

  /* ---------- helpers ---------- */
  var HEART_PATH = 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z';
  var HEART_OUTLINE =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="' + HEART_PATH + '"/></svg>';
  var HEART_FILLED =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="' + HEART_PATH + '"/></svg>';

  function currentProductHandle() {
    var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  /* ---------- product-page heart ---------- */
  function buildHeart(handle, opts) {
    opts = opts || {};
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'kuvrd-wishlist-btn' + (opts.className ? ' ' + opts.className : '');
    btn.setAttribute('data-wishlist-handle', handle);
    btn.setAttribute('aria-pressed', has(handle) ? 'true' : 'false');
    paintHeart(btn);
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var added = toggle(handle);
      btn.setAttribute('aria-pressed', added ? 'true' : 'false');
      paintHeart(btn);
    });
    return btn;
  }
  function paintHeart(btn) {
    var on = btn.getAttribute('aria-pressed') === 'true';
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
    btn.innerHTML = (on ? HEART_FILLED : HEART_OUTLINE) +
      '<span class="kuvrd-wishlist-btn__label">' + (on ? 'Saved' : 'Add to Wishlist') + '</span>';
  }

  function injectProductHeart() {
    var handle = currentProductHandle();
    if (!handle) return;
    if (document.querySelector('.kuvrd-wishlist-btn--pdp[data-wishlist-handle="' + cssEscape(handle) + '"]')) return;
    // Main product info column only — never the sticky add-to-cart bar.
    var atc =
      document.querySelector('.product-information--inner product-form [name="add"]') ||
      document.querySelector('.product-information--inner #AddToCart');
    if (!atc) return;
    if (atc.closest('[data-sticky="true"], .product-add-to-cart-sticky, .thb-sticky-atc')) return;
    // Insert as its own full-width row BELOW the buy-buttons container so it
    // never shares the quantity / add-to-cart / dynamic-checkout flex row.
    var container = atc.closest('.product-add-to-cart-container') || atc.closest('.add_to_cart_holder') || atc.parentElement;
    if (!container) return;
    var heart = buildHeart(handle, { className: 'kuvrd-wishlist-btn--pdp' });
    container.insertAdjacentElement('afterend', heart);
  }

  function cssEscape(s) {
    return (window.CSS && CSS.escape) ? CSS.escape(s) : s.replace(/["\\\]]/g, '\\$&');
  }

  /* ---------- header count ---------- */
  function updateHeaderCount() {
    var n = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('is-empty', n === 0);
    });
  }

  /* ---------- wishlist page ---------- */
  function renderWishlistPage() {
    var root = document.getElementById('kuvrd-wishlist-app');
    if (!root) return;
    var list = read();
    if (!list.length) {
      root.innerHTML = '<p class="kuvrd-wishlist-empty">Your wishlist is empty. Tap the heart on any product to save it here.</p>';
      return;
    }
    root.innerHTML = '<div class="kuvrd-wishlist-grid"></div>';
    var grid = root.firstChild;
    list.forEach(function (handle) {
      fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p) { remove(handle); return; }
          var img = (p.featured_image || (p.images && p.images[0]) || '');
          if (img && img.indexOf('//') === 0) img = 'https:' + img;
          var card = document.createElement('div');
          card.className = 'kuvrd-wishlist-card';
          card.innerHTML =
            '<a class="kuvrd-wishlist-card__media" href="/products/' + encodeURIComponent(handle) + '">' +
              (img ? '<img loading="lazy" src="' + img + '&width=400" alt="' + escapeHtml(p.title) + '">' : '') +
            '</a>' +
            '<a class="kuvrd-wishlist-card__title" href="/products/' + encodeURIComponent(handle) + '">' + escapeHtml(p.title) + '</a>' +
            '<button type="button" class="kuvrd-wishlist-card__remove" data-remove="' + escapeHtml(handle) + '">Remove</button>';
          grid.appendChild(card);
        })
        .catch(function () {});
    });
    grid.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-remove]');
      if (!btn) return;
      remove(btn.getAttribute('data-remove'));
      var card = btn.closest('.kuvrd-wishlist-card');
      if (card) card.remove();
      if (!read().length) renderWishlistPage();
    });
  }
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- styles ---------- */
  function injectStyles() {
    var css =
      '.kuvrd-wishlist-btn{display:inline-flex;align-items:center;justify-content:center;gap:10px;background:transparent;border:1px solid rgba(0,0,0,.2);border-radius:0;padding:0 20px;min-height:50px;cursor:pointer;color:#151515;font:inherit;line-height:1;transition:color .15s,border-color .15s,background .15s;-webkit-appearance:none;appearance:none}' +
      '.kuvrd-wishlist-btn>svg{flex:none;display:block}' +
      '.kuvrd-wishlist-btn:hover{border-color:#151515}' +
      '.kuvrd-wishlist-btn.is-active{color:#151515;border-color:#151515}' +
      '.kuvrd-wishlist-btn--pdp{display:flex;width:100%;flex:1 1 100%;margin:12px 0 0;position:relative;z-index:1;clear:both}' +
      '.kuvrd-wishlist-btn__label{font-size:12px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;white-space:nowrap}' +
      '.kuvrd-wishlist-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:24px}' +
      '@media(max-width:900px){.kuvrd-wishlist-grid{grid-template-columns:repeat(2,1fr)}}' +
      '.kuvrd-wishlist-card{display:flex;flex-direction:column;gap:8px}' +
      '.kuvrd-wishlist-card__media img{width:100%;height:auto;display:block}' +
      '.kuvrd-wishlist-card__title{font-size:14px;text-decoration:none;color:inherit}' +
      '.kuvrd-wishlist-card__remove{align-self:flex-start;background:none;border:none;padding:0;cursor:pointer;font-size:12px;text-decoration:underline;color:inherit;opacity:.7}' +
      '.kuvrd-wishlist-empty{text-align:center;padding:60px 20px;opacity:.7}';
    var style = document.getElementById('kuvrd-wishlist-styles');
    if (!style) {
      style = document.createElement('style');
      style.id = 'kuvrd-wishlist-styles';
      document.head.appendChild(style);
    }
    style.textContent = css;
  }

  /* ---------- init ---------- */
  function init() {
    injectStyles();
    injectProductHeart();
    updateHeaderCount();
    renderWishlistPage();
  }
  document.addEventListener('wishlist:change', function () {
    updateHeaderCount();
    document.querySelectorAll('.kuvrd-wishlist-btn').forEach(function (btn) {
      var on = has(btn.getAttribute('data-wishlist-handle'));
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      paintHeart(btn);
    });
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-run heart injection after theme section reloads (variant switches, etc.)
  document.addEventListener('shopify:section:load', init);
})();
